import { useCallback } from 'react';
import { generateNewDeviceId } from '../../../utils/deviceInfo/getDeviceId';
import { store } from '../../../store/Store';
import { reloadApp } from '../../../utils/reloadApp';
import { getStoredSMLThemePreference, storeSMLThemePreference } from '../../../modules/music24/theme/smlTheme';
import { SMLRouteNames } from '../../../routing/sml/SMLRoutes';
import { revokeSessionOnServer } from '../../../modules/app/session/refreshSessionToken';
import { storeTourActive } from '../../../modules/onboarding/tour/tourSession';
import { removeSignedInHint } from '../../../modules/app/session/signedInHintStore';

/**
 * Clears the session, then calls `leave` once, after the store is cleared and a new device id
 * exists. `leave` runs even when that fails, so the person is never stuck signed in.
 *
 * With the session token, it also ends every session of the user on the server (all devices),
 * so a copied token stops working at once.
 */
function useSignOut(leave: () => void, userToken: string | null | undefined): () => void {
  return useCallback(() => {
    if (userToken !== null && userToken !== undefined && userToken !== '') {
      revokeSessionOnServer(userToken);
    }
    // music24.com stops the greeting at once (the store clear below also removes the note).
    removeSignedInHint();
    // A sign-out ends the LaziiBee tour of this tab.
    storeTourActive(false);
    // The color theme choice (`system`, `light` or `dark`) is a device preference that survives
    // logout. On web, store.clear() maps to localStorage.clear(), which wipes every key (including
    // the theme), so capture it before clearing and restore it afterwards.
    const savedTheme = getStoredSMLThemePreference();
    const restoreTheme = () => {
      if (savedTheme !== null) {
        storeSMLThemePreference(savedTheme);
      }
    };
    store
      .clear()
      .then(() => {
        restoreTheme();
        return generateNewDeviceId();
      })
      .catch(console.error)
      .then(() => leave())
      .catch(console.error);
    // store.clear() clears localStorage synchronously on web, so restore the theme right away
    // too: nothing reads a theme-less store in between.
    restoreTheme();
  }, [leave, userToken]);
}

/** Logs out and reloads the app. */
export const useOnLogout = (userToken?: string | null) => useSignOut(reloadApp, userToken);

/**
 * Web only: Music24 has no desktop build (`music24-app` is web only), so this sets
 * `window.location` directly instead of a platform-specific `reloadApp` variant.
 *
 * Signs out, then opens sign-in with `returnTo`, so the next sign-in comes back to `/app/{returnTo}`.
 * `sessionStorage` is not cleared, so a token kept there (a team invite) survives.
 */
export const useSignOutAndReturnTo = (returnTo: string, userToken?: string | null) => {
  const openSignIn = useCallback(() => {
    window.location.href = `/app/${SMLRouteNames.auth}?returnTo=${encodeURIComponent(returnTo)}`;
  }, [returnTo]);
  return useSignOut(openSignIn, userToken);
};

/** Closes the tab when the browser allows it (a tab that a script opened); else loads the signed-out app. */
function closeTabOrReload(): void {
  window.close();
  // `window.close()` does nothing in a tab that the person opened by hand. Then this shows the
  // normal signed-out screen. In a tab that closes, the navigation never happens.
  reloadApp();
}

/**
 * Ends an admin session ("Log in as this user"): the normal sign-out, then the tab closes when it
 * can. The API does not end the sessions of the user for a token with `loginAsAdmin`.
 */
export const useEndLoginAsSession = (userToken?: string | null) => useSignOut(closeTabOrReload, userToken);
